import { Injectable, inject, signal, computed } from '@angular/core';
import { injectQuery, queryOptions } from '@ngneat/query';
import { derivedAsync } from 'ngxtension/derived-async';
import { MessagingService, GetMessagesParams } from './messaging.service';
import { SearchState } from '../../search/services/search.state';
import { Message, MessageType } from '../models/messaging.models';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';
import dayjs from 'dayjs';

@Injectable({
  providedIn: 'root',
})
export class MessagingState {
  private readonly messagingService = inject(MessagingService);
  private readonly searchState = inject(SearchState);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly query = injectQuery();

  readonly selectedTypes = signal<MessageType[]>(['EMAIL', 'SMS', 'Whatsapp']);
  readonly searchTerm = signal<string>('');
  readonly pageSize = signal<number>(8);

  readonly tenant = computed(() => this.tenantUrlUtil.currentTenant());
  readonly documentType = computed(() => this.tenantUrlUtil.documentType());
  readonly documentValue = computed(() => this.tenantUrlUtil.documentValue());
  readonly dateRange = computed(() => this.tenantUrlUtil.dateRangeForAPI());
  readonly clientEmail = computed(() => this.searchState.clientData()?.email || '');

  private getMessagesQueryOptions(params: GetMessagesParams) {
    const isIdentityDocument = params.documentType === 'IDENTITY_DOCUMENT';
    const hasRequiredEmail = isIdentityDocument ? !!params.email : true;
    
    return queryOptions({
      queryKey: ['messages', params.tenant, params.documentType, params.documentValue, params.dateRange, params.email] as const,
      queryFn: () => this.messagingService.getMessages(params),
      enabled: !!params.tenant && params.documentType !== 'UNKNOWN' && !!params.documentValue && hasRequiredEmail,
    });
  }

  readonly messagesQuery = derivedAsync(() => {
    const tenant = this.tenant();
    const documentType = this.documentType();
    const documentValue = this.documentValue();
    const dateRange = this.dateRange();
    const clientEmail = this.clientEmail();

    if (!tenant || documentType === 'UNKNOWN' || !documentValue) {
      return undefined;
    }

    return this.query(this.getMessagesQueryOptions({ 
      tenant, 
      documentType, 
      documentValue,
      dateRange,
      email: clientEmail
    })).result$;
  });

  readonly isLoading = computed(() => {
    const query = this.messagesQuery();
    return query?.isLoading ?? false;
  });

  readonly error = computed(() => {
    const query = this.messagesQuery();
    return query?.error ?? null;
  });

  readonly allMessages = computed(() => {
    const query = this.messagesQuery();
    return query?.data?.data ?? [];
  });

  readonly filteredMessages = computed(() => {
    const messages = this.allMessages();
    const types = this.selectedTypes();
    const search = this.searchTerm().toLowerCase();

    let filtered = messages;

    const allAvailableTypes: MessageType[] = ['EMAIL', 'SMS', 'Whatsapp'];
    const hasAllTypes = allAvailableTypes.every((type) => types.includes(type));

    if (!hasAllTypes && types.length > 0) {
      filtered = filtered.filter((msg) => {
        const messageType = msg.tipoMensaje.toLowerCase();
        return types.some((type) => {
          if (type.toLowerCase() === 'whatsapp') {
            return messageType.includes('whatsapp');
          }
          return type.toLowerCase() === messageType;
        });
      });
    }

    if (search) {
      filtered = filtered.filter(
        (msg) =>
          msg.emailId.toLowerCase().includes(search) ||
          msg.templateName.toLowerCase().includes(search) ||
          msg.templateDescription.toLowerCase().includes(search) ||
          msg.destinatario.toLowerCase().includes(search)
      );
    }

    // Sort messages by date (most recent first)
    filtered = filtered.sort((a, b) => {
      const dateA = dayjs(a.fecha, 'DD/MM/YYYY HH:mm:ss');
      const dateB = dayjs(b.fecha, 'DD/MM/YYYY HH:mm:ss');

      if (!dateA.isValid() && !dateB.isValid()) return 0;
      if (!dateA.isValid()) return 1;
      if (!dateB.isValid()) return -1;

      return dateB.diff(dateA);
    });

    return filtered;
  });

  setSelectedTypes(types: MessageType[]) {
    this.selectedTypes.set(types);
  }

  setSearchTerm(term: string) {
    this.searchTerm.set(term);
  }

  async prefetchMessages(email: string) {
    if (!email) return;
    // Prefetching is handled automatically by @ngneat/query
    // when the email signal changes
  }
}
